import { Injectable } from '@angular/core';
import { HttpService } from '../http/http.service';
import { firstValueFrom } from 'rxjs';
import { 
  LedgerHealthReport, 
  LedgerHealthApiResponse,
  LedgerAlert, 
  HealthSummary,
  HealthDiagnostic,
  SupplierHealthIssue
} from '../../interface/ledger/ledger-system';

@Injectable({
  providedIn: 'root'
})
export class LedgerMonitoringService {
  constructor(private http: HttpService) { }

  // Health monitoring
  async getSystemHealth(): Promise<LedgerHealthReport> {
    const apiResponse: LedgerHealthApiResponse = await firstValueFrom(this.http.get('/ledgermonitoring/health'));
    return this.transformHealthResponse(apiResponse);
  }

  // Transform API response to UI-friendly format
  private transformHealthResponse(apiResponse: LedgerHealthApiResponse): LedgerHealthReport {
    const diagnostics = this.generateDiagnostics(apiResponse);
    const recommendations = this.generateRecommendations(apiResponse);
    const supplierIssues = this.analyzeSupplierIssues(apiResponse);
    
    return {
      checkTime: apiResponse.checkTime,
      overallHealth: this.getStatusName(apiResponse.overallHealth),
      lastChecked: apiResponse.checkTime,
      salesLedgerHealth: {
        status: this.getStatusName(apiResponse.salesLedgerHealth.status),
        totalInvoices: apiResponse.salesLedgerHealth.totalInvoices,
        invoiceLedgerEntries: apiResponse.salesLedgerHealth.invoiceLedgerEntries,
        missingInvoiceEntries: apiResponse.salesLedgerHealth.missingInvoiceEntries,
        totalPayments: apiResponse.salesLedgerHealth.totalPayments,
        paymentLedgerEntries: apiResponse.salesLedgerHealth.paymentLedgerEntries,
        missingPaymentEntries: apiResponse.salesLedgerHealth.missingPaymentEntries,
        customersWithNegativeBalances: apiResponse.salesLedgerHealth.customersWithNegativeBalances,
        errorCount: apiResponse.salesLedgerHealth.missingInvoiceEntries + apiResponse.salesLedgerHealth.missingPaymentEntries
      },
      purchaseLedgerHealth: {
        status: this.getStatusName(apiResponse.purchaseLedgerHealth.status),
        totalInvoices: apiResponse.purchaseLedgerHealth.totalInvoices,
        invoiceLedgerEntries: apiResponse.purchaseLedgerHealth.invoiceLedgerEntries,
        missingInvoiceEntries: apiResponse.purchaseLedgerHealth.missingInvoiceEntries,
        totalPayments: apiResponse.purchaseLedgerHealth.totalPayments,
        paymentLedgerEntries: apiResponse.purchaseLedgerHealth.paymentLedgerEntries,
        missingPaymentEntries: apiResponse.purchaseLedgerHealth.missingPaymentEntries,
        suppliersWithNegativeBalances: apiResponse.purchaseLedgerHealth.suppliersWithNegativeBalances,
        errorCount: apiResponse.purchaseLedgerHealth.missingInvoiceEntries + apiResponse.purchaseLedgerHealth.missingPaymentEntries
      },
      orphanedRecordsCheck: {
        status: this.getStatusName(apiResponse.orphanedRecordsCheck.status),
        orphanedSaleInvoiceEntries: apiResponse.orphanedRecordsCheck.orphanedSaleInvoiceEntries,
        orphanedSalePaymentEntries: apiResponse.orphanedRecordsCheck.orphanedSalePaymentEntries,
        orphanedPurchaseInvoiceEntries: apiResponse.orphanedRecordsCheck.orphanedPurchaseInvoiceEntries,
        orphanedPurchasePaymentEntries: apiResponse.orphanedRecordsCheck.orphanedPurchasePaymentEntries
      },
      balanceConsistencyCheck: {
        status: this.getStatusName(apiResponse.balanceConsistencyCheck.status),
        customersWithInconsistentBalances: apiResponse.balanceConsistencyCheck.customersWithInconsistentBalances,
        suppliersWithInconsistentBalances: apiResponse.balanceConsistencyCheck.suppliersWithInconsistentBalances
      },
      criticalIssues: apiResponse.criticalIssues,
      warnings: apiResponse.warnings,
      diagnostics,
      recommendations,
      supplierIssues,
      criticalCount: diagnostics.filter(d => d.severity === 'Critical').length,
      warningCount: diagnostics.filter(d => d.severity === 'Warning').length,
      healthyCount: this.countHealthyComponents(apiResponse)
    };
  }

  private getStatusName(status: number): 'Healthy' | 'Warning' | 'Critical' {
    switch (status) {
      case 0: return 'Healthy';
      case 1: return 'Warning';
      case 2: return 'Critical';
      default: return 'Critical';
    }
  }

  private generateDiagnostics(apiResponse: LedgerHealthApiResponse): HealthDiagnostic[] {
    const diagnostics: HealthDiagnostic[] = [];

    // Purchase Ledger Payment Issues
    if (apiResponse.purchaseLedgerHealth.missingPaymentEntries > 0) {
      diagnostics.push({
        issueType: 'missing_payment_ledger',
        severity: apiResponse.purchaseLedgerHealth.status === 2 ? 'Critical' : 'Warning',
        description: `${apiResponse.purchaseLedgerHealth.missingPaymentEntries} purchase payment(s) missing ledger entries`,
        affectedCount: apiResponse.purchaseLedgerHealth.missingPaymentEntries,
        recommendedActions: [
          'Run ledger migration to create missing payment entries',
          'Use supplier sync endpoint to fix specific suppliers',
          'Verify payment records in the database'
        ],
        details: {
          totalPayments: apiResponse.purchaseLedgerHealth.totalPayments,
          paymentLedgerEntries: apiResponse.purchaseLedgerHealth.paymentLedgerEntries,
          missingCount: apiResponse.purchaseLedgerHealth.missingPaymentEntries
        }
      });
    }

    // Purchase Ledger Invoice Issues
    if (apiResponse.purchaseLedgerHealth.missingInvoiceEntries > 0) {
      diagnostics.push({
        issueType: 'missing_invoice_ledger',
        severity: apiResponse.purchaseLedgerHealth.status === 2 ? 'Critical' : 'Warning',
        description: `${apiResponse.purchaseLedgerHealth.missingInvoiceEntries} purchase invoice(s) missing ledger entries`,
        affectedCount: apiResponse.purchaseLedgerHealth.missingInvoiceEntries,
        recommendedActions: [
          'Run ledger migration to create missing invoice entries',
          'Check invoice creation process',
          'Verify invoice records in the database'
        ]
      });
    }

    // Sales Ledger Payment Issues
    if (apiResponse.salesLedgerHealth.missingPaymentEntries > 0) {
      diagnostics.push({
        issueType: 'missing_payment_ledger',
        severity: apiResponse.salesLedgerHealth.status === 2 ? 'Critical' : 'Warning',
        description: `${apiResponse.salesLedgerHealth.missingPaymentEntries} sales payment(s) missing ledger entries`,
        affectedCount: apiResponse.salesLedgerHealth.missingPaymentEntries,
        recommendedActions: [
          'Run ledger migration to create missing payment entries',
          'Use customer sync endpoint to fix specific customers',
          'Verify payment records in the database'
        ]
      });
    }

    // Orphaned Records
    const totalOrphaned = apiResponse.orphanedRecordsCheck.orphanedSaleInvoiceEntries +
      apiResponse.orphanedRecordsCheck.orphanedSalePaymentEntries +
      apiResponse.orphanedRecordsCheck.orphanedPurchaseInvoiceEntries +
      apiResponse.orphanedRecordsCheck.orphanedPurchasePaymentEntries;
    
    if (totalOrphaned > 0) {
      diagnostics.push({
        issueType: 'orphaned_records',
        severity: apiResponse.orphanedRecordsCheck.status === 2 ? 'Critical' : 'Warning',
        description: `${totalOrphaned} orphaned ledger entries detected`,
        affectedCount: totalOrphaned,
        recommendedActions: [
          'Clean up orphaned ledger entries',
          'Verify data integrity',
          'Run reconciliation process'
        ]
      });
    }

    return diagnostics;
  }

  private generateRecommendations(apiResponse: LedgerHealthApiResponse): string[] {
    const recommendations: string[] = [];

    if (apiResponse.overallHealth === 1) {
      recommendations.push('System is in warning state - immediate attention recommended');
    } else if (apiResponse.overallHealth === 2) {
      recommendations.push('System is in critical state - urgent action required');
    }

    if (apiResponse.purchaseLedgerHealth.missingPaymentEntries > 0) {
      recommendations.push(`Run migration to create ${apiResponse.purchaseLedgerHealth.missingPaymentEntries} missing payment ledger entries`);
      recommendations.push('Use POST /api/v1/ledgerreconciliation/suppliers/{supplierId}/sync to fix specific suppliers');
    }

    if (apiResponse.purchaseLedgerHealth.missingInvoiceEntries > 0) {
      recommendations.push('Verify invoice creation process and run migration if needed');
    }

    if (recommendations.length === 0) {
      recommendations.push('System health is optimal - no action required');
    }

    return recommendations;
  }

  private analyzeSupplierIssues(apiResponse: LedgerHealthApiResponse): SupplierHealthIssue[] {
    const issues: SupplierHealthIssue[] = [];

    if (apiResponse.purchaseLedgerHealth.missingPaymentEntries > 0) {
      issues.push({
        issueType: 'missing_payment_ledger',
        missingCount: apiResponse.purchaseLedgerHealth.missingPaymentEntries,
        totalCount: apiResponse.purchaseLedgerHealth.totalPayments,
        recommendedAction: 'Use supplier sync endpoint to create missing payment ledger entries'
      });
    }

    if (apiResponse.purchaseLedgerHealth.missingInvoiceEntries > 0) {
      issues.push({
        issueType: 'missing_invoice_ledger',
        missingCount: apiResponse.purchaseLedgerHealth.missingInvoiceEntries,
        totalCount: apiResponse.purchaseLedgerHealth.totalInvoices,
        recommendedAction: 'Run migration to create missing invoice ledger entries'
      });
    }

    return issues;
  }

  private countHealthyComponents(apiResponse: LedgerHealthApiResponse): number {
    let healthyCount = 0;
    if (apiResponse.salesLedgerHealth.status === 0) healthyCount++;
    if (apiResponse.purchaseLedgerHealth.status === 0) healthyCount++;
    if (apiResponse.orphanedRecordsCheck.status === 0) healthyCount++;
    if (apiResponse.balanceConsistencyCheck.status === 0) healthyCount++;
    return healthyCount;
  }

  // Get health summary for dashboard
  async getHealthStatus(): Promise<HealthSummary> {
    return firstValueFrom(this.http.get('/ledgermonitoring/status'));
  }

  // Service health check
  async pingService(): Promise<string> {
    return firstValueFrom(this.http.get('/ledgermonitoring/ping'));
  }

  // Alert management
  async getAlerts(): Promise<LedgerAlert[]> {
    return firstValueFrom(this.http.get('/ledgermonitoring/alerts'));
  }
}